import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import Compressor from "compressorjs";

export default class extends Controller {
    static targets = ["fileInput", "previewContainer", "deletedPhotoIdsInput", "fileSizeWarning", "fileLimitWarning", "photosScrollContainer", "addPhotoPlaceholder", "placeholderIcon", "emptyState", "labelText", "addPhotoLabel"];
    static values = {
        photoWidth: { type: String, default: "w-[60%] lg:w-[40%]" },
        parameterName: { type: String, default: "diary_entry[photos][]" },
        fileLimit: { type: Number, default: 0 }
    };

    connect() {
        this.photos = []; // Holds photo objects with { file, signedId, uploadProgress, uploadStatus }
        this.deletedPhotoIds = []; // Holds IDs of existing photos marked for deletion
        // If the hidden input already has values (e.g., validation error), parse them
        if (this.hasDeletedPhotoIdsInputTarget && this.deletedPhotoIdsInputTarget.value) {
            this.deletedPhotoIds = this.deletedPhotoIdsInputTarget.value.split(',').filter(id => id);
        }
        // Initial UI state is set by ERB, only update when photos change
        this.directUploadUrl = this.fileInputTarget.dataset.directUploadUrl;
    }

    disconnect() {
        // Clean up all object URLs to prevent memory leaks
        this.photos.forEach(photoObj => {
            if (photoObj.previewUrl) {
                URL.revokeObjectURL(photoObj.previewUrl);
            }
        });
    }

    async handleFileUpload(event) {
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
        let allFilesValid = true;
        const validFiles = [];

        Array.from(event.target.files).forEach(file => {
            if (file.size > MAX_FILE_SIZE) {
                allFilesValid = false;
            } else {
                validFiles.push(file);
            }
        });

        if (!allFilesValid) {
            // Clear the file input if any file is invalid to prevent re-processing invalid files
            // and allow the user to select different files.
            this.fileInputTarget.value = "";
            if (this.hasFileSizeWarningTarget) {
                this.fileSizeWarningTarget.classList.remove('hidden');
            }
            return; // Stop processing if any file is too large
        }

        if (this.hasFileSizeWarningTarget) {
            this.fileSizeWarningTarget.classList.add('hidden');
        }

        // Filter out duplicates
        const uniqueNewFiles = validFiles.filter(newFile =>
            !this.photos.some(existingPhoto =>
                existingPhoto.file.name === newFile.name &&
                existingPhoto.file.size === newFile.size &&
                existingPhoto.file.lastModified === newFile.lastModified
            )
        );

        // Check file limit if set
        let filesToAdd = uniqueNewFiles;
        let filesRejected = false;

        if (this.fileLimitValue > 0) {
            const availableSlots = this.getAvailableSlots();
            if (availableSlots <= 0) {
                this.fileInputTarget.value = "";
                this.showFileLimitWarning();
                return;
            }

            if (uniqueNewFiles.length > availableSlots) {
                filesToAdd = uniqueNewFiles.slice(0, availableSlots);
                filesRejected = true;
            }
        }

        // Create photo objects immediately
        const newPhotos = [];
        filesToAdd.forEach(file => {
            const photoObj = {
                file: file,
                originalFile: file,
                signedId: null,
                uploadProgress: 0,
                uploadStatus: 'compressing' // compressing, uploading, complete, error
            };
            this.photos.push(photoObj);
            newPhotos.push(photoObj);
        });

        if (filesRejected) {
            this.showFileLimitWarning();
        } else if (this.hasFileLimitWarningTarget) {
            this.fileLimitWarningTarget.classList.add('hidden');
        }

        // Clear the file input to allow selecting the same files again if needed
        this.fileInputTarget.value = "";

        // Process all photos in parallel - non-blocking
        this.processPhotosInParallel(newPhotos);
    }

    async processPhotosInParallel(newPhotos) {
        // Render all previews immediately
        await Promise.all(newPhotos.map(photoObj => this.renderSinglePhoto(photoObj)));

        // Compress images one by one to avoid overwhelming the browser
        for (const photoObj of newPhotos) {
            const compressedFile = await this.compressImage(photoObj.file);
            photoObj.file = compressedFile;
            photoObj.uploadStatus = 'pending';

            // Start uploading as soon as this compression is done
            this.startDirectUpload(photoObj);
        }
    }

    compressImage(file) {
        return new Promise((resolve) => {
            new Compressor(file, {
                quality: 0.7,
                maxWidth: 2048,
                maxHeight: 2048,
                mimeType: 'image/jpeg',
                convertSize: 5000000, // Convert PNG to JPEG if larger than 5MB
                success(compressedFile) {
                    resolve(compressedFile);
                },
                error(err) {
                    console.error('Compression error:', err);
                    // If compression fails, use original file
                    resolve(file);
                }
            });
        });
    }

    startDirectUpload(photoObj) {
        photoObj.uploadStatus = 'uploading';

        const upload = new DirectUpload(photoObj.file, this.directUploadUrl, {
            directUploadWillStoreFileWithXHR: (request) => {
                request.upload.addEventListener("progress", event => {
                    const progress = (event.loaded / event.total) * 100;
                    photoObj.uploadProgress = progress;
                    this.updatePhotoPreview(photoObj);
                });
            }
        });

        upload.create((error, blob) => {
            if (error) {
                console.error("Upload error:", error);
                photoObj.uploadStatus = 'error';
                this.updatePhotoPreview(photoObj);
            } else {
                photoObj.signedId = blob.signed_id;
                photoObj.uploadStatus = 'complete';
                photoObj.uploadProgress = 100;
                this.updatePhotoPreview(photoObj);
                this.createHiddenBlobInput(blob.signed_id);
            }
        });
    }

    createHiddenBlobInput(signedId) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = this.parameterNameValue;
        input.value = signedId;
        input.dataset.newPhotoBlob = 'true';
        this.previewContainerTarget.appendChild(input);
    }

    removeNewPhoto(event) {
        const index = parseInt(event.params.index, 10);
        if (isNaN(index) || index < 0 || index >= this.photos.length) {
            console.error("Invalid index for removeNewPhoto:", event.params.index);
            return;
        }

        const photoObj = this.photos[index];

        // Revoke the object URL to free memory
        if (photoObj.previewUrl) {
            URL.revokeObjectURL(photoObj.previewUrl);
        }

        // If the photo was uploaded, remove its hidden input
        if (photoObj.signedId) {
            const hiddenInput = this.previewContainerTarget.querySelector(
                `input[type="hidden"][value="${photoObj.signedId}"]`
            );
            if (hiddenInput) {
                hiddenInput.remove();
            }
        }

        // Remove from internal array to prevent re-rendering if renderPhotos is called again
        this.photos.splice(index, 1);
        // Remove the preview element itself
        event.currentTarget.closest('.new-photo-preview').remove();

        this.triggerHapticFeedback(event.currentTarget);
        this.updateUI();
    }

    deleteExistingPhoto(event) {
        const photoId = event.params.photoId;
        if (!photoId) {
            console.error("Missing photoId for deleteExistingPhoto");
            return;
        }
        const photoElement = this.previewContainerTarget.querySelector(`#photo-${photoId}`);
        if (photoElement) {
            photoElement.remove(); // Remove the preview and its associated hidden field
            if (!this.deletedPhotoIds.includes(photoId)) {
                this.deletedPhotoIds.push(photoId);
            }
            this.updateDeletedPhotoIdsInput();
            this.triggerHapticFeedback(event.currentTarget);
            this.updateUI();
        }
    }

    renderSinglePhoto(photoObj) {
        return new Promise((resolve) => {
            // Use requestAnimationFrame to avoid blocking
            requestAnimationFrame(() => {
                // Check if we need to transition from empty state to scroll container
                if (this.hasEmptyStateTarget && !this.hasPhotosScrollContainerTarget) {
                    this.transitionToScrollContainer();
                }

                if (!this.hasPhotosScrollContainerTarget) {
                    resolve();
                    return;
                }

                const index = this.photos.indexOf(photoObj);
                const addPhotoLabel = this.photosScrollContainerTarget.querySelector('label[for="photo-upload"]');
                const photoElement = document.createElement('div');

                // Use createObjectURL instead of FileReader - much faster!
                const fileToPreview = photoObj.originalFile || photoObj.file;
                const objectUrl = URL.createObjectURL(fileToPreview);

                // Store the URL so we can revoke it later
                photoObj.previewUrl = objectUrl;

                const uploadIndicator = this.getUploadIndicatorHTML(photoObj);

                photoElement.className = `aspect-square flex-none shadow-card bg-white ${this.photoWidthValue} ring-1 ring-black/[2%] rounded-2xl p-2 snap-center new-photo-preview`;
                photoElement.dataset.newPhotoIndex = index;
                photoElement.innerHTML = `
                    <div class="h-full w-full bg-warm-gray-100 rounded-2xl relative overflow-hidden">
                        <img src="${objectUrl}" alt="New photo preview" class="w-full h-full object-cover rounded-2xl">
                        ${uploadIndicator}
                        <button type="button"
                                class="delete-new-photo touch-effect trigger-native-haptic absolute top-2 right-2 aspect-square size-5 flex items-center justify-center bg-red-500 text-white rounded-full p-1"
                                data-action="click->photo-management#removeNewPhoto"
                                data-photo-management-index-param="${index}">
                          -
                        </button>
                    </div>
                `;

                if (addPhotoLabel) {
                    this.photosScrollContainerTarget.insertBefore(photoElement, addPhotoLabel);
                } else {
                    this.photosScrollContainerTarget.appendChild(photoElement);
                }

                this.updateUI();
                resolve();
            });
        });
    }

    renderPhotos() {
        // Check if we need to transition from empty state to scroll container
        if (this.hasEmptyStateTarget && !this.hasPhotosScrollContainerTarget) {
            this.transitionToScrollContainer();
        }

        if (!this.hasPhotosScrollContainerTarget) {
            return; // Exit if scroll container doesn't exist yet
        }

        // Remove only previews generated by this controller (new photos)
        this.photosScrollContainerTarget.querySelectorAll('.new-photo-preview').forEach(el => el.remove());

        // Get the add photo label to insert new photos before it
        const addPhotoLabel = this.photosScrollContainerTarget.querySelector('label[for="photo-upload"]');

        this.photos.forEach((photoObj, index) => {
            requestAnimationFrame(() => {
                const photoElement = document.createElement('div');

                // Use createObjectURL instead of FileReader - much faster!
                const fileToPreview = photoObj.originalFile || photoObj.file;
                const objectUrl = photoObj.previewUrl || URL.createObjectURL(fileToPreview);

                // Store the URL if we just created it
                if (!photoObj.previewUrl) {
                    photoObj.previewUrl = objectUrl;
                }

                const uploadIndicator = this.getUploadIndicatorHTML(photoObj);

                // All photos use the same consistent styling
                photoElement.className = `aspect-square flex-none shadow-card bg-white ${this.photoWidthValue} ring-1 ring-black/[2%] rounded-2xl p-2 snap-center new-photo-preview`;
                photoElement.dataset.newPhotoIndex = index;
                photoElement.innerHTML = `
                    <div class="h-full w-full bg-warm-gray-100 rounded-2xl relative overflow-hidden">
                        <img src="${objectUrl}" alt="New photo preview" class="w-full h-full object-cover rounded-2xl">
                        ${uploadIndicator}
                        <button type="button"
                                class="delete-new-photo touch-effect trigger-native-haptic absolute top-2 right-2 aspect-square size-5 flex items-center justify-center bg-red-500 text-white rounded-full p-1"
                                data-action="click->photo-management#removeNewPhoto"
                                data-photo-management-index-param="${index}">
                          -
                        </button>
                    </div>
                `;

                // Insert before the add photo label
                if (addPhotoLabel) {
                    this.photosScrollContainerTarget.insertBefore(photoElement, addPhotoLabel);
                } else {
                    this.photosScrollContainerTarget.appendChild(photoElement);
                }
            });
        });

        this.updateUI();
    }

    transitionToScrollContainer() {
        // Create the scroll container structure
        const scrollContainer = document.createElement('div');
        scrollContainer.className = 'flex gap-4 overflow-x-scroll scrollbar-none pb-2 snap-x snap-mandatory justify-center';
        scrollContainer.dataset.photoManagementTarget = 'photosScrollContainer';

        // Move the file input to a new label in the scroll container
        const fileInput = this.fileInputTarget;
        const addPhotoLabel = document.createElement('label');
        addPhotoLabel.className = `aspect-square flex-none shadow-card bg-white ${this.photoWidthValue} ring-1 ring-black/[2%] rounded-2xl cursor-pointer text-warm-gray-600 lg:hover:border-indigo-800 lg:hover:text-indigo-800 p-2 snap-center`;
        addPhotoLabel.htmlFor = 'photo-upload';
        addPhotoLabel.dataset.photoManagementTarget = 'addPhotoLabel';

        const placeholder = document.createElement('div');
        placeholder.className = 'h-full w-full bg-warm-gray-100 rounded-2xl relative overflow-hidden flex items-center justify-center';
        placeholder.dataset.photoManagementTarget = 'addPhotoPlaceholder';

        // Get the SVG icon from the empty state
        const placeholderIcon = this.emptyStateTarget.querySelector('svg');
        if (placeholderIcon) {
            placeholder.appendChild(placeholderIcon.cloneNode(true));
        }

        addPhotoLabel.appendChild(placeholder);
        addPhotoLabel.appendChild(fileInput);

        scrollContainer.appendChild(addPhotoLabel);

        // Replace empty state with scroll container
        this.emptyStateTarget.replaceWith(scrollContainer);
    }

    getUploadIndicatorHTML(photoObj) {
        if (photoObj.uploadStatus === 'complete') {
            return '';
        }

        if (photoObj.uploadStatus === 'error') {
            return `
                <div class="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                    <span class="text-white text-sm font-semibold">Upload failed</span>
                </div>
            `;
        }

        if (photoObj.uploadStatus === 'compressing' || photoObj.uploadStatus === 'uploading' || photoObj.uploadStatus === 'pending') {
            return `
                <div class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <div class="flex gap-1.5">
                        <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                        <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                        <div class="w-2 h-2 bg-white rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                    </div>
                </div>
            `;
        }

        return '';
    }

    updatePhotoPreview(photoObj) {
        const index = this.photos.indexOf(photoObj);
        if (index === -1) return;

        if (!this.hasPhotosScrollContainerTarget) return;

        const previewElement = this.photosScrollContainerTarget.querySelector(
            `.new-photo-preview[data-new-photo-index="${index}"]`
        );

        if (!previewElement) return;

        // Update or remove the upload indicator
        const existingIndicator = previewElement.querySelector('.absolute.inset-0.bg-black, .absolute.inset-0.bg-red-500');
        if (existingIndicator && photoObj.uploadStatus !== 'complete') {
            existingIndicator.outerHTML = this.getUploadIndicatorHTML(photoObj);
        } else if (existingIndicator && photoObj.uploadStatus === 'complete') {
            existingIndicator.remove();
        }
    }

    updateDeletedPhotoIdsInput() {
        if (this.hasDeletedPhotoIdsInputTarget) {
            this.deletedPhotoIdsInputTarget.value = this.deletedPhotoIds.join(',');
        }
    }

    updateUI() {
        // Count existing photos that haven't been deleted
        const existingPhotos = this.previewContainerTarget.querySelectorAll('[id^="photo-"]');
        const existingPhotosCount = existingPhotos.length;
        const totalPhotos = existingPhotosCount + this.photos.length;

        // If we're in scroll container mode and all photos are deleted, transition back to empty state
        if (this.hasPhotosScrollContainerTarget && totalPhotos === 0) {
            this.transitionToEmptyState();
            return;
        }

        // Handle file limit UI updates
        if (this.fileLimitValue > 0) {
            const atLimit = totalPhotos >= this.fileLimitValue;

            // Hide or show the add photo button
            if (this.hasAddPhotoLabelTarget) {
                if (atLimit) {
                    this.addPhotoLabelTarget.classList.add('hidden');
                } else {
                    this.addPhotoLabelTarget.classList.remove('hidden');
                }
            }

            // Update label text if in empty state
            if (this.hasLabelTextTarget && this.hasEmptyStateTarget) {
                this.labelTextTarget.textContent = `Add photos (${totalPhotos}/${this.fileLimitValue})`;
            }
        }
    }

    transitionToEmptyState() {
        // Create the empty state structure
        const emptyState = document.createElement('label');
        emptyState.className = `aspect-square flex-none shadow-card bg-white max-${this.photoWidthValue} mx-auto rotate-3 ring-1 ring-black/[2%] rounded-2xl cursor-pointer text-warm-gray-600 lg:hover:border-indigo-800 lg:hover:text-indigo-800 p-2 w-full flex flex-col items-center justify-center`;
        emptyState.htmlFor = 'photo-upload';
        emptyState.dataset.photoManagementTarget = 'emptyState';

        const placeholder = document.createElement('div');
        placeholder.className = 'h-full w-full bg-warm-gray-100 rounded-2xl relative overflow-hidden flex items-center justify-center';

        // Get the SVG icon from the add photo placeholder if it exists
        const addPhotoPlaceholder = this.photosScrollContainerTarget.querySelector('[data-photo-management-target="addPhotoPlaceholder"]');
        if (addPhotoPlaceholder) {
            const svg = addPhotoPlaceholder.querySelector('svg');
            if (svg) {
                svg.dataset.photoManagementTarget = 'placeholderIcon';
                placeholder.appendChild(svg);
            }
        }

        const label = document.createElement('span');
        label.className = 'text-xs mt-3 pointer-events-none';
        label.dataset.photoManagementTarget = 'labelText';
        label.textContent = 'Add photos';

        // Move file input back
        const fileInput = this.fileInputTarget;

        emptyState.appendChild(placeholder);
        emptyState.appendChild(label);
        emptyState.appendChild(fileInput);

        // Replace scroll container with empty state
        this.photosScrollContainerTarget.replaceWith(emptyState);
    }

    triggerHapticFeedback(element) {
        if (element && element.classList.contains('trigger-native-haptic')) {
            triggerHaptic();
        }
    }

    getCurrentPhotoCount() {
        // Count existing photos that haven't been deleted
        const existingPhotos = this.previewContainerTarget.querySelectorAll('[id^="photo-"]');
        return existingPhotos.length + this.photos.length;
    }

    getAvailableSlots() {
        if (this.fileLimitValue <= 0) {
            return Infinity; // No limit
        }
        return this.fileLimitValue - this.getCurrentPhotoCount();
    }

    showFileLimitWarning() {
        if (this.hasFileLimitWarningTarget) {
            this.fileLimitWarningTarget.classList.remove('hidden');
        }
    }
} 